// Refusals from the native (e-mail + password) door, #279. Mirrors API.Core/Authorization/AuthErrorCodes.cs.
//
// The app has no i18n layer, so every user-facing sentence is a German literal in a map keyed on the server's
// stable error CODE — never the server's `description`, which is English by design and is a developer-facing
// string. That rule is the style guide's, and it is why this file exists rather than the components rendering
// `err.error.description`.

/** The error codes the native door can answer with. */
export const NativeAccountErrorCodes = {
  /** One code for every way a sign-in can fail — see the note on the message below. */
  CredentialsInvalid: 'E_AUTH_LOCAL_CREDENTIALS_INVALID',
  AccountLocked: 'E_AUTH_LOCAL_ACCOUNT_LOCKED',
  UsernameInvalid: 'E_AUTH_LOCAL_USERNAME_INVALID',
  PasswordWeak: 'E_AUTH_LOCAL_PASSWORD_WEAK',
  AccountRequired: 'E_AUTH_LOCAL_ACCOUNT_REQUIRED',
  /** Not native, but reachable from the same forms: the account exists and was switched off. */
  AccountDeactivated: 'E_AUTH_ACCOUNT_DEACTIVATED',
  /**
   * A claim or link, started while a platform owner impersonates that login (#854). Answered by the e-mail form
   * and by the three provider buttons alike; only the person themselves may attach an account to their login.
   */
  ClaimWhileImpersonating: 'E_AUTH_CLAIM_WHILE_IMPERSONATING'
} as const;

export type NativeAccountErrorCode =
  typeof NativeAccountErrorCodes[keyof typeof NativeAccountErrorCodes];

/**
 * The legacy-login hint, shown after **every** failed e-mail + password attempt.
 *
 * ## Why it is on the generic refusal and not on a smarter condition
 *
 * During the migration most people still hold a legacy account and no native one, so the likeliest reason this
 * form failed is that they belong on the other one. Saying so is the only thing that connects the two forms —
 * without it, an employee typing their work address into the primary form is simply told they are wrong, with
 * no way to discover the legacy button is what they want.
 *
 * It must appear for **every** failure, unconditionally: the server answers one code whether the address is
 * unknown, belongs to a federated account, or merely had the wrong password, and a hint that appeared only for
 * addresses that exist would leak exactly what that single code exists to hide.
 *
 * ## Why it names BOTH migrations
 *
 * There are two ways off the legacy login, not one: an account is either turned into a **native** account (this
 * form's e-mail + password) or has a **provider** login linked to it (Google, Microsoft, Apple — the three
 * buttons directly below this message on the same screen). A legacy account can be un-migrated on either side,
 * and usually is un-migrated on both.
 *
 * The first wording named only the native direction, which quietly excluded the larger group: somebody whose
 * administrator had told them "you will sign in with your Microsoft account" read a sentence about a switch to
 * e-mail and passwords, decided it was not about them, and had no reason left to press the legacy button. The
 * hint therefore describes the **situation** — an account that has not been switched over yet — rather than one
 * direction out of it.
 *
 * It cannot say *which* of the two applies to the person reading it, and must not try: that answer is exactly
 * the account-existence fact the single refusal code exists to withhold. Naming both is the only honest form of
 * the sentence.
 *
 * ## How it is retired
 *
 * It is scaffolding for the migration window, not a permanent part of the form — a login that permanently says
 * "maybe try the other one" is a worse form than either login alone. It comes out when the legacy login does,
 * which is **#264** (the retirement of the legacy authorization system; see ADR #260's phase table). Concretely:
 * delete {@link LEGACY_LOGIN_HINT} and the concatenation below, leaving the first sentence as the whole message.
 * Nothing else references it, and the spec that pins it names #264 too.
 */
/**
 * Why no account can be linked right now (#854): the session is a platform owner working on behalf of a
 * customer, and only the customer may attach an account to their own login. One sentence for all four doors, so
 * the e-mail form and the provider buttons cannot explain the same refusal differently.
 */
export const CLAIM_WHILE_IMPERSONATING_MESSAGE =
  'Während Sie im Namen einer anderen Person arbeiten, können Sie kein Konto mit diesem Zugang verknüpfen. ' +
  'Das muss die Person selbst in ihrer eigenen Sitzung tun.';

export const LEGACY_LOGIN_HINT =
  ' Falls Sie ein bestehendes CheqMe-Konto haben, das noch nicht umgestellt ist, nutzen Sie bitte die ' +
  'Anmeldung für bestehende Konten.';

const MESSAGES: Record<string, string> = {
  [NativeAccountErrorCodes.CredentialsInvalid]:
    'E-Mail-Adresse oder Passwort ist falsch.',
  [NativeAccountErrorCodes.AccountLocked]:
    'Zu viele Fehlversuche. Dieses Konto ist für kurze Zeit gesperrt — bitte versuchen Sie es in einigen ' +
    'Minuten erneut.',
  [NativeAccountErrorCodes.UsernameInvalid]:
    'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
  [NativeAccountErrorCodes.PasswordWeak]:
    'Das Passwort muss 8–32 Zeichen lang sein und einen Großbuchstaben, einen Kleinbuchstaben, eine Ziffer ' +
    'sowie eines der Sonderzeichen ! @ # $ & * enthalten.',
  [NativeAccountErrorCodes.AccountRequired]:
    'Dieses Konto meldet sich über einen Anbieter an und hat hier kein Passwort. Ändern Sie es bei dem ' +
    'Anbieter, mit dem Sie sich anmelden.',
  [NativeAccountErrorCodes.AccountDeactivated]:
    'Dieses Konto wurde deaktiviert. Bitten Sie um eine neue Einladung — Ihr Konto bleibt dabei erhalten.',
  [NativeAccountErrorCodes.ClaimWhileImpersonating]: CLAIM_WHILE_IMPERSONATING_MESSAGE
};

/**
 * The German sentence for a provider door (Microsoft, Apple, Google) whose start the server refused, or `null`
 * when the refusal is not one the reader can be told anything specific about.
 *
 * Unlike {@link describeNativeAccountError} there is no generic fallback: an unknown failure of a provider start
 * is infrastructure, and the host reports that its own way. Only a refusal with a reason the reader should know
 * gets a sentence here, and today that is one (#854).
 */
export function describeProviderStartRefusal(code: string | null | undefined): string | null {
  return code === NativeAccountErrorCodes.ClaimWhileImpersonating ? CLAIM_WHILE_IMPERSONATING_MESSAGE : null;
}

/**
 * The German sentence for a native-door refusal.
 *
 * Returns the generic refusal for an unknown or missing code, deliberately: a new server code must degrade to a
 * sentence somebody can act on rather than to a blank banner or a raw identifier.
 */
export function describeNativeAccountError(code: string | null | undefined): string {
  return (code && MESSAGES[code]) || MESSAGES[NativeAccountErrorCodes.CredentialsInvalid];
}

/**
 * The sentence for a failed sign-in on the **primary** form, with the migration-period legacy hint appended.
 *
 * Separate from {@link describeNativeAccountError} because the hint belongs to one screen and one moment. The
 * invitation landing must not carry it — somebody redeeming an invitation is creating an account, and pointing
 * them at a legacy login they may not have is noise at the worst time.
 */
export function describeNativeSignInError(code: string | null | undefined): string {
  return `${describeNativeAccountError(code)}${LEGACY_LOGIN_HINT}`;
}
